// src/pages/GradesPage/hooks/useGradeSelection.js

import { useState, useEffect, useRef } from "react";
import {
	useLazyGetLazyGradesBySubjectQuery,
	useLazyGetLazyGradesByStudentQuery,
} from "../../../store/api";
import { VIEW_MODES, getCurrentSemester, SEMESTER_MONTHS } from "../constants";

const getCurrentSemesterData = (semesters, selectedSemester) => {
	if (!semesters || semesters.length === 0) return null;

	const targetSemesterName =
		selectedSemester === 1 ? "First Semester" : "Second Semester";

	const matchingSemester = semesters.find(
		(s) => s.semesterName === targetSemesterName
	);

	return matchingSemester || semesters[0];
};

export const useGradeSelection = (departmentId) => {
	const [viewMode, setViewMode] = useState(VIEW_MODES.SUBJECTS);
	const [selectedSubject, setSelectedSubject] = useState(null);
	const [selectedStudent, setSelectedStudent] = useState(null);
	const [selectedSemester, setSelectedSemester] = useState(
		getCurrentSemester()
	);
	const [grades, setGrades] = useState({});
	const [searchTerm, setSearchTerm] = useState("");
	const [isDelayedLoading, setIsDelayedLoading] = useState(false);

	const actionStartTimeRef = useRef(null);
	const delayTimeoutRef = useRef(null);
	const maxDelayTimeoutRef = useRef(null);
	const previousSelectionRef = useRef({
		subject: null,
		student: null,
		semester: null,
	});

	const [
		triggerSubjectGrades,
		{
			data: subjectGradesData,
			isLoading: isLoadingSubjectGrades,
			error: subjectGradesError,
			isSuccess: isSubjectGradesSuccess,
		},
	] = useLazyGetLazyGradesBySubjectQuery();

	const [
		triggerStudentGrades,
		{
			data: studentGradesData,
			isLoading: isLoadingStudentGrades,
			error: studentGradesError,
			isSuccess: isStudentGradesSuccess,
		},
	] = useLazyGetLazyGradesByStudentQuery();

	const clearAllTimeouts = () => {
		if (delayTimeoutRef.current) {
			clearTimeout(delayTimeoutRef.current);
			delayTimeoutRef.current = null;
		}
		if (maxDelayTimeoutRef.current) {
			clearTimeout(maxDelayTimeoutRef.current);
			maxDelayTimeoutRef.current = null;
		}
	};

	const endLoadingDelay = () => {
		if (!actionStartTimeRef.current) return;

		const elapsedTime = Date.now() - actionStartTimeRef.current;
		const minDelay = 200;

		clearAllTimeouts();

		if (elapsedTime < minDelay) {
			delayTimeoutRef.current = setTimeout(() => {
				setIsDelayedLoading(false);
				actionStartTimeRef.current = null;
			}, minDelay - elapsedTime);
		} else {
			setIsDelayedLoading(false);
			actionStartTimeRef.current = null;
		}
	};

	const startLoadingDelay = () => {
		clearAllTimeouts();
		actionStartTimeRef.current = Date.now();
		setIsDelayedLoading(true);

		maxDelayTimeoutRef.current = setTimeout(() => {
			setIsDelayedLoading(false);
			actionStartTimeRef.current = null;
		}, 3000);
	};

	useEffect(() => {
		// Reset all selections when department changes
		setViewMode(VIEW_MODES.SUBJECTS);
		setSelectedSubject(null);
		setSelectedStudent(null);
		setGrades({});
		setSearchTerm("");
		setIsDelayedLoading(false);

		// Clear all timeouts
		clearAllTimeouts();
		actionStartTimeRef.current = null;

		// Reset previous selection ref
		previousSelectionRef.current = {
			subject: null,
			student: null,
			semester: selectedSemester,
		};
	}, [departmentId]);

	useEffect(() => {
		if (isSubjectGradesSuccess || subjectGradesError) {
			endLoadingDelay();
		}
	}, [isSubjectGradesSuccess, subjectGradesError]);

	useEffect(() => {
		if (isStudentGradesSuccess || studentGradesError) {
			endLoadingDelay();
		}
	}, [isStudentGradesSuccess, studentGradesError]);

	useEffect(() => {
		if (subjectGradesData && selectedSubject) {
			const processedGrades = {};
			const semesterData = getCurrentSemesterData(
				subjectGradesData.semesters,
				selectedSemester
			);

			if (semesterData && semesterData.months) {
				const currentSemesterMonths = SEMESTER_MONTHS[selectedSemester].map(
					(m) => m.id
				);

				semesterData.months
					.filter((month) => currentSemesterMonths.includes(month.monthNumber))
					.forEach((month) => {
						if (month.assessments) {
							month.assessments.forEach((assessment) => {
								if (assessment.studentGrades) {
									assessment.studentGrades.forEach((studentGrade) => {
										if (
											studentGrade.points !== null &&
											studentGrade.points !== undefined
										) {
											const studentId = studentGrade.studentId;
											const monthId = month.monthNumber;

											if (!processedGrades[studentId]) {
												processedGrades[studentId] = {};
											}
											if (!processedGrades[studentId][monthId]) {
												processedGrades[studentId][monthId] = {
													month: monthId,
													assessments: [],
													total: 0,
												};
											}

											processedGrades[studentId][monthId].assessments.push({
												id: assessment.assessmentId,
												name: assessment.title,
												points: studentGrade.points,
												maxPoints: assessment.maxPoints,
												type: assessment.type,
												date: assessment.date,
											});

											processedGrades[studentId][monthId].total +=
												studentGrade.points;
										}
									});
								}
							});
						}
					});
			}

			setGrades(processedGrades);
		}
	}, [subjectGradesData, selectedSubject, selectedSemester]);

	useEffect(() => {
		if (studentGradesData && selectedStudent) {
			const processedGrades = {};
			const semesterData = getCurrentSemesterData(
				studentGradesData.semesters,
				selectedSemester
			);

			if (semesterData && semesterData.subjects) {
				const currentSemesterMonths = SEMESTER_MONTHS[selectedSemester].map(
					(m) => m.id
				);

				semesterData.subjects.forEach((subject) => {
					if (subject.months) {
						subject.months
							.filter((month) =>
								currentSemesterMonths.includes(month.monthNumber)
							)
							.forEach((month) => {
								if (month.assessments) {
									month.assessments.forEach((assessment) => {
										if (
											assessment.studentPoints !== null &&
											assessment.studentPoints !== undefined
										) {
											const studentId = selectedStudent.id;
											const monthId = month.monthNumber;
											const subjectKey = `${studentId}_${subject.subjectId}`;

											if (!processedGrades[subjectKey]) {
												processedGrades[subjectKey] = {
													subjectId: subject.subjectId,
													subjectName: subject.subjectName,
													months: {},
												};
											}
											if (!processedGrades[subjectKey].months[monthId]) {
												processedGrades[subjectKey].months[monthId] = {
													month: monthId,
													assessments: [],
													total: 0,
												};
											}

											processedGrades[subjectKey].months[
												monthId
											].assessments.push({
												id: assessment.assessmentId,
												name: assessment.title,
												points: assessment.studentPoints,
												maxPoints: assessment.maxPoints,
												type: assessment.type,
												date: assessment.date,
											});

											processedGrades[subjectKey].months[monthId].total +=
												assessment.studentPoints;
										}
									});
								}
							});
					}
				});
			}

			setGrades(processedGrades);
		}
	}, [studentGradesData, selectedStudent, selectedSemester]);

	const handleViewModeChange = (mode) => {
		clearAllTimeouts();
		setIsDelayedLoading(false);
		actionStartTimeRef.current = null;

		setViewMode(mode);
		setSelectedSubject(null);
		setSelectedStudent(null);
		setSearchTerm("");
		setGrades({});

		previousSelectionRef.current = {
			subject: null,
			student: null,
			semester: selectedSemester,
		};
	};

	const handleSubjectSelect = (subject) => {
		if (selectedSubject?.id === subject.id) return;

		startLoadingDelay();

		setSelectedSubject(subject);
		setGrades({});

		previousSelectionRef.current = {
			...previousSelectionRef.current,
			subject: subject.id,
		};

		if (subject && departmentId) {
			triggerSubjectGrades({
				subjectId: subject.id,
				departmentId: departmentId,
			});
		}
	};

	const handleStudentSelect = (student) => {
		if (selectedStudent?.id === student.id) return;

		startLoadingDelay();

		setSelectedStudent(student);
		setGrades({});

		previousSelectionRef.current = {
			...previousSelectionRef.current,
			student: student.id,
		};

		if (student) {
			triggerStudentGrades(student.id);
		}
	};

	const handleSemesterChange = (semester) => {
		setSelectedSemester(semester);
		setGrades({});

		previousSelectionRef.current = {
			...previousSelectionRef.current,
			semester: semester,
		};

		// Re-trigger the appropriate query if we have a selection
		if (viewMode === VIEW_MODES.SUBJECTS && selectedSubject && departmentId) {
			startLoadingDelay();
			triggerSubjectGrades({
				subjectId: selectedSubject.id,
				departmentId: departmentId,
			});
		} else if (viewMode === VIEW_MODES.STUDENTS && selectedStudent) {
			startLoadingDelay();
			triggerStudentGrades(selectedStudent.id);
		}
	};

	return {
		viewMode,
		selectedSubject,
		selectedStudent,
		selectedSemester,
		grades,
		searchTerm,
		isLoadingGrades: isDelayedLoading,
		gradesError:
			viewMode === VIEW_MODES.SUBJECTS
				? subjectGradesError
				: studentGradesError,
		setSelectedSemester,
		setSearchTerm,
		handleViewModeChange,
		handleSubjectSelect,
		handleStudentSelect,
		handleSemesterChange,
	};
};
